import { Link } from 'react-router-dom';
import { Bookmark, Trash2, Play, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import useQuranStore from '../store/quranStore';

export default function BookmarksPage() {
  const { bookmarks, removeBookmark } = useQuranStore();

  // Sort by most recent
  const sortedBookmarks = [...bookmarks].sort((a, b) => b.timestamp - a.timestamp);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen" data-testid="bookmarks-page">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Bookmark className="w-5 h-5 text-primary" />
            </div>
            <h1 className="font-serif text-3xl font-bold">Saved Verses</h1>
          </div>
          <p className="text-muted-foreground">
            {bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''} saved
          </p>
        </div>

        {/* Empty state */}
        {bookmarks.length === 0 && (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Bookmark className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl font-semibold mb-2">No bookmarks yet</h3>
            <p className="text-muted-foreground mb-6">
              Save verses while reading to find them here later
            </p>
            <Link to="/">
              <Button data-testid="start-reading-btn">
                <BookOpen className="w-4 h-4 mr-2" />
                Start Reading
              </Button>
            </Link>
          </div>
        )}

        {/* Bookmarks list */}
        {bookmarks.length > 0 && (
          <div className="space-y-4">
            {sortedBookmarks.map((bookmark, index) => (
              <div
                key={`${bookmark.surahNo}-${bookmark.ayahNo}`}
                className="group bg-card rounded-xl border p-5 hover:border-primary/50 transition-all"
                data-testid={`bookmark-item-${index}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Surah info */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {bookmark.surahNo}:{bookmark.ayahNo}
                      </span>
                      <span className="text-sm font-medium">{bookmark.surahName}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(bookmark.timestamp)}
                      </span>
                    </div>

                    {/* Arabic text */}
                    <p className="font-arabic text-xl text-right leading-loose mb-3 truncate">
                      {bookmark.arabic}
                    </p>

                    {/* English translation */}
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {bookmark.english}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link to={`/surah/${bookmark.surahNo}?ayah=${bookmark.ayahNo}`}>
                      <Button variant="ghost" size="icon" data-testid={`goto-bookmark-${index}`}>
                        <Play className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBookmark(bookmark.surahNo, bookmark.ayahNo)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-bookmark-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
